import { RefObject } from 'react';
import LinkIcon from '../icons/LinkIcon';
import Loading from '../loading/Loading';
import { CourseOption, EnrollGroup } from '../../types/course.response';
import { getSemesterName } from '../../utils/semesterUtils';
import EnrollGroupDetail from './EnrollGroupInfoBar';

interface CourseRowDetailProps {
    courseOption: CourseOption;
    selectedSemester: string;
    cornellClassesUrl: string | null;
    truncatedText: string;
    containerRef: RefObject<HTMLDivElement | null>;
    availabilityText: React.ReactNode | null;
    enrollGroups: EnrollGroup[];
    isPending: boolean;
    onMarkAsTaken: () => void;
    onAddToSchedule: () => void;
    onDeleteFromSchedule: () => void;
    userCourses?: CourseOption[];
}

function CourseRowDetail({
    courseOption,
    selectedSemester,
    cornellClassesUrl,
    truncatedText,
    containerRef,
    availabilityText,
    enrollGroups,
    isPending,
    onMarkAsTaken,
    onAddToSchedule,
    onDeleteFromSchedule,
    userCourses,
}: CourseRowDetailProps) {
    return (
        <tr>
            <td colSpan={4} className="px-4 pb-6 border-b">
                <div className="pl-8 text-gray-700 text-xs md:text-sm leading-relaxed">
                    {courseOption.type === 'EXAM' ? (
                        <div className="py-4">
                            {courseOption.userState.status === 'COMPLETED' ? (
                                <span className="text-green-600 font-medium cursor-default">
                                    {courseOption.userState.semester && courseOption.userState.semester !== 'unspecified'
                                        ? `✓ Completed in ${getSemesterName(courseOption.userState.semester)}`
                                        : '✓ Completed'}
                                </span>
                            ) : (
                                <button
                                    className="text-blue-600 hover:text-blue-700 cursor-pointer disabled:opacity-50"
                                    onClick={onMarkAsTaken}
                                    disabled={isPending}
                                >
                                    {isPending ? 'Adding...' : "I've taken this exam"}
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            {availabilityText && (
                                <div className="text-red-500 italic mb-2">
                                    {availabilityText}
                                </div>
                            )}
                            <div
                                ref={containerRef}
                                className="hidden md:block text-gray-700 leading-relaxed py-2"
                                style={{ visibility: truncatedText ? 'visible' : 'hidden' }}
                            >
                                {truncatedText && (
                                    <>
                                        {truncatedText}
                                        {truncatedText !== courseOption.courseInfo.description && (
                                            <>
                                                {'... '}
                                                {cornellClassesUrl && (
                                                    <a
                                                        href={cornellClassesUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        Read more
                                                        <LinkIcon
                                                            size={14}
                                                            color="currentColor"
                                                        />
                                                    </a>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                            {courseOption.courseInfo.prereq && (
                                <div className="hidden md:block text-gray-700 py-2">
                                    <span className="font-semibold">Prerequisite:</span> {courseOption.courseInfo.prereq}
                                </div>
                            )}
                            {courseOption.courseInfo.coreq && (
                                <div className="hidden md:block text-gray-700 py-2">
                                    <span className="font-semibold">Corequisite:</span> {courseOption.courseInfo.coreq}
                                </div>
                            )}
                            {courseOption.courseInfo.enrollmentPriority && (
                                <div className="hidden md:block text-gray-700 py-2">
                                    <span className="font-semibold">Enrollment Priority:</span> {courseOption.courseInfo.enrollmentPriority}
                                </div>
                            )}
                            {courseOption.courseInfo.forbiddenOverlaps && (
                                <div className="hidden md:block text-gray-700 py-2">
                                    <span className="font-semibold">Forbidden Overlaps:</span> {courseOption.courseInfo.forbiddenOverlaps}
                                </div>
                            )}
                            {courseOption.courseInfo.courseAttributes.length > 0 && (
                                <div className="hidden md:block text-gray-700 py-2">
                                    <span className="font-semibold">Distribution Requirements:</span> {courseOption.courseInfo.courseAttributes.map(ca => ca.attributeValue).join(', ')}
                                </div>
                            )}
                            {courseOption.courseInfo.fee && (
                                <div className="hidden md:block text-gray-700 py-2">
                                    <span className="font-semibold">Fee:</span> {courseOption.courseInfo.fee}
                                </div>
                            )}
                            {enrollGroups.some(eg => eg.combinedGroupId) && courseOption.combinedCourseInfo?.combinedCourseIds?.length > 0 && (
                                <div className="hidden md:block text-gray-700 py-2">
                                    <span className="font-semibold">Combined with:</span> {courseOption.combinedCourseInfo.combinedCourseIds.map(id => id.replace(/^([A-Z]+)(\d+)$/, '$1 $2')).join(', ')}
                                </div>
                            )}
                            {enrollGroups.length > 0 && (
                                <div className="py-2">
                                    {enrollGroups.map(eg => (
                                        <EnrollGroupDetail key={eg.id} enrollGroup={eg} userCourses={userCourses} />
                                    ))}
                                </div>
                            )}
                            <div className="py-4 flex flex-wrap items-center gap-2 text-sm">
                                {courseOption.userState.status === 'COMPLETED' ? (
                                    <span className="text-green-600 font-medium cursor-default">
                                        {courseOption.userState.semester && courseOption.userState.semester !== 'unspecified'
                                            ? `✓ Completed in ${getSemesterName(courseOption.userState.semester)}`
                                            : '✓ Completed'}
                                    </span>
                                ) : courseOption.userState.status !== 'IN_PROGRESS' && courseOption.userState.status !== 'PLANNED' ? (
                                    <button
                                        className="text-blue-600 hover:text-blue-700 cursor-pointer disabled:opacity-50"
                                        onClick={onMarkAsTaken}
                                        disabled={isPending}
                                    >
                                        {isPending ? 'Adding...' : "I've taken this course"}
                                    </button>
                                ) : null}
                                <div className="flex items-center gap-2 flex-wrap md:ml-auto">
                                    {cornellClassesUrl && (
                                        <a
                                            href={cornellClassesUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="border border-gray-300 rounded-lg px-3 py-1.5 md:px-4 md:py-2 text-gray-600 hover:text-gray-800 hover:border-gray-400 inline-flex items-center gap-1 transition-colors"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            View on Class Roster
                                            <LinkIcon size={13} color="currentColor" />
                                        </a>
                                    )}
                                    {selectedSemester !== 'All' && (
                                        <>
                                            {(() => {
                                                if (courseOption.userState.status === 'COMPLETED' || courseOption.userState.status === 'IN_PROGRESS' || courseOption.userState.status === 'PLANNED') {
                                                    return (
                                                        <button
                                                            className="border border-red-300 rounded-lg px-3 py-1.5 md:px-4 md:py-2 text-red-600 hover:text-red-700 hover:border-red-400 cursor-pointer disabled:opacity-50 transition-colors"
                                                            onClick={onDeleteFromSchedule}
                                                            disabled={isPending}
                                                        >
                                                            {isPending ? 'Removing...' : 'Remove'}
                                                        </button>
                                                    );
                                                }
                                                if (!courseOption.courseState.isSemesterAvailable || !courseOption.courseState.isLocationAvailable) {
                                                    return (
                                                        <button className="hidden md:block border rounded-lg px-3 py-1.5 md:px-4 md:py-2 bg-gray-300 text-white cursor-not-allowed">
                                                            + Add to schedule
                                                        </button>
                                                    );
                                                }
                                                return (
                                                    <button
                                                        className="hidden md:flex border rounded-lg px-3 py-1.5 md:px-4 md:py-2 bg-blue-600 hover:bg-blue-700 text-white cursor-pointer disabled:bg-blue-400 items-center gap-2"
                                                        onClick={onAddToSchedule}
                                                        disabled={isPending}
                                                    >
                                                        {isPending && <Loading size="sm" className="brightness-200" />}
                                                        {isPending ? 'Adding...' : '+ Add to schedule'}
                                                    </button>
                                                );
                                            })()}
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
}

export default CourseRowDetail;
